import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Activity, Upload, FileText, Bell, LogOut, User, Download, Globe, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { patientAPI, fileAPI, paymentAPI, notificationAPI } from '../utils/api';
import CaseUploadWizard from '../components/CaseUploadWizard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientDashboard({ user, onLogout }) {
  const [cases, setCases] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCase, setExpandedCase] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCases();
    fetchNotifications();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await patientAPI.getCases();
      setCases(response.data);
    } catch (error) {
      toast.error('Не удалось загрузить кейсы');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  const handleDownloadReport = async (caseItem) => {
    // Use pdf_file_id from report, not report_id
    const fileId = caseItem.pdf_file_id;
    if (!fileId) {
      toast.error('Отчет еще не готов');
      return;
    }
    
    try {
      const url = `${BACKEND_URL}/api/files/${fileId}`;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${caseItem.id}.pdf`);
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Отчет скачивается');
    } catch (error) {
      toast.error('Не удалось скачать отчет');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'in_review': 'bg-blue-50 text-blue-700 border-blue-200',
      'waiting': 'bg-amber-50 text-amber-700 border-amber-200',
      'pending_payment': 'bg-orange-50 text-orange-700 border-orange-200',
      'pending_admin_review': 'bg-purple-50 text-purple-700 border-purple-200',
      'report_submitted': 'bg-cyan-50 text-cyan-700 border-cyan-200',
      'declined': 'bg-red-50 text-red-700 border-red-200',
      'refunded': 'bg-gray-50 text-gray-700 border-gray-200'
    };
    return colors[status] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getStatusText = (status) => {
    const translations = {
      'pending_admin_review': 'На рассмотрении',
      'pending_payment': 'Ожидает оплаты',
      'waiting': 'Ожидает врача',
      'in_review': 'На проверке',
      'report_submitted': 'Отчет готовится',
      'completed': 'Завершено',
      'declined': 'Отклонено',
      'refunded': 'Возврат средств'
    };
    return translations[status] || status;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'declined': 
      case 'refunded': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Activity className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold text-slate-900">www.vtoroemnenie.com</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              className="relative p-2 hover:bg-slate-100 rounded-lg"
              onClick={() => toast.info(`${notifications.filter(n => !n.read).length} непрочитанных уведомлений`)}
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full"></span>
              )}
            </button>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <User className="w-4 h-4 text-slate-600" />
              <span className="text-sm font-medium">{user.first_name} {user.last_name}</span>
            </div>
            <Button onClick={onLogout} variant="ghost" className="text-slate-600">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <Routes>
        <Route path="/" element={
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6 bg-white rounded-xl">
                <div className="text-sm text-slate-500 uppercase mb-2">Всего кейсов</div>
                <div className="text-3xl font-bold">{cases.length}</div>
              </Card>
              <Card className="p-6 bg-white rounded-xl">
                <div className="text-sm text-slate-500 uppercase mb-2">На проверке</div>
                <div className="text-3xl font-bold">
                  {cases.filter(c => ['in_review', 'waiting', 'pending_admin_review', 'report_submitted'].includes(c.status)).length}
                </div>
              </Card>
              <Card className="p-6 bg-white rounded-xl">
                <div className="text-sm text-slate-500 uppercase mb-2">Завершено</div>
                <div className="text-3xl font-bold">
                  {cases.filter(c => c.status === 'completed').length}
                </div>
              </Card>
            </div>

            {/* Action Button */}
            <div className="mb-8">
              <Button
                onClick={() => navigate('/patient/upload')}
                className="bg-primary text-white hover:bg-primary/90 h-11 px-6 rounded-full font-medium"
              >
                <Upload className="w-4 h-4 mr-2" />
                Запросить новое второе мнение
              </Button>
            </div>

            {/* Cases List */}
            <div>
              <h2 className="text-2xl font-bold mb-6">Ваши кейсы</h2>
              {loading ? (
                <div className="text-center py-12 text-slate-600">Загрузка...</div>
              ) : cases.length === 0 ? (
                <Card className="p-12 bg-white text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">Вы еще не отправили ни одного кейса.</p>
                  <Button onClick={() => navigate('/patient/upload')} className="bg-primary text-white rounded-full">
                    Загрузить первый кейс
                  </Button>
                </Card>
              ) : (
                <div className="space-y-4">
                  {cases.map((caseItem) => (
                    <Card key={caseItem.id} className="bg-white rounded-xl overflow-hidden">
                      {/* Case Header */}
                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold">{caseItem.exam_type}</h3>
                              <Badge className={`${getStatusColor(caseItem.status)} px-2.5 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1`}>
                                {getStatusIcon(caseItem.status)}
                                {getStatusText(caseItem.status)}
                              </Badge>
                              {caseItem.is_urgent && (
                                <Badge className="bg-red-100 text-red-700 border-red-200">СРОЧНО</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">Врач: {caseItem.doctor_name}</p>
                            <p className="text-xs text-slate-400 mt-1">ID: {caseItem.id}</p>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold">${caseItem.price}</div>
                            <div className="text-xs text-slate-500">
                              {new Date(caseItem.created_at).toLocaleDateString('ru-RU')}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-3">
                          {caseItem.status === 'pending_payment' && (
                            <Button
                              onClick={async () => {
                                try {
                                  await paymentAPI.confirm(caseItem.id);
                                  toast.success('Оплата подтверждена');
                                  fetchCases();
                                } catch (error) {
                                  toast.error('Ошибка оплаты');
                                }
                              }}
                              className="bg-primary text-white rounded-full text-sm px-4 h-9"
                            >
                              Завершить оплату
                            </Button>
                          )}
                          
                          {caseItem.status === 'completed' && caseItem.pdf_file_id && (
                            <Button
                              onClick={() => handleDownloadReport(caseItem)}
                              className="bg-primary text-white rounded-full text-sm px-4 h-9"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Скачать PDF отчет
                            </Button>
                          )}

                          {caseItem.status === 'completed' && caseItem.translation && (
                            <Button
                              onClick={() => setExpandedCase(expandedCase === caseItem.id ? null : caseItem.id)}
                              variant="outline"
                              className="rounded-full text-sm px-4 h-9"
                            >
                              <Globe className="w-4 h-4 mr-1" />
                              Просмотреть перевод ({caseItem.translation.target_language})
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Expanded Translation View */}
                      {expandedCase === caseItem.id && caseItem.translation && (
                        <div className="border-t border-slate-100 bg-slate-50 p-6">
                          <div className="flex items-center gap-2 mb-4">
                            <Globe className="w-5 h-5 text-primary" />
                            <h4 className="font-semibold">Переведенный отчет ({caseItem.translation.target_language})</h4>
                            <span className="text-xs text-slate-500">
                              {new Date(caseItem.translation.translated_at).toLocaleString('ru-RU')}
                            </span>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="bg-white p-4 rounded-lg border border-slate-200">
                              <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Результаты исследования</h5>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{caseItem.translation.translated_findings}</p>
                            </div>
                            
                            <div className="bg-white p-4 rounded-lg border border-slate-200">
                              <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Заключение</h5>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{caseItem.translation.translated_impression}</p>
                            </div>
                            
                            <div className="bg-white p-4 rounded-lg border border-slate-200">
                              <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2">Рекомендации</h5>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{caseItem.translation.translated_recommendations}</p>
                            </div>
                          </div>

                          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-xs text-amber-800">
                              <strong>Важно:</strong> Это перевод медицинского заключения. Официальным документом является оригинальный отчет на английском языке. 
                              Пожалуйста, проконсультируйтесь с вашим лечащим врачом перед принятием медицинских решений.
                            </p>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        } />
        <Route path="/upload" element={<CaseUploadWizard onComplete={() => { fetchCases(); navigate('/patient'); }} />} />
      </Routes>
    </div>
  );
}
